import { useCallback, useMemo } from "react";
import { Source, Layer } from "react-map-gl/mapbox";
import type { MapMouseEvent } from "react-map-gl/mapbox";
import type { FeatureCollection, Point, Feature } from "geojson";
import type { RelatedPlace } from "~/lib/api";
import { RELATED_PLACES_LAYER_ID, RELATED_PLACES_SOURCE_ID, relatedPlacesLayout } from "~/lib/mapConstants";
import { useMapEvent } from "../hooks/useMapEvent";
import { useCursorOnHover } from "../hooks/useCursorOnHover";

interface RelatedPlacesLayerProps {
  relatedPlaces: RelatedPlace[];
  onClick?: (place: RelatedPlace) => void;
}

type RelatedPlaceFeature = Feature<Point, { id: string; type: string }>;

function toGeoJSON(relatedPlaces: RelatedPlace[]): FeatureCollection<Point> {
  return {
    type: "FeatureCollection",
    features: relatedPlaces.map(
      (place): RelatedPlaceFeature => ({
        type: "Feature",
        id: place.id,
        geometry: {
          type: "Point",
          coordinates: [place.longitude, place.latitude],
        },
        properties: {
          id: place.id,
          type: place.type,
        },
      }),
    ),
  };
}

/**
 * Renders related places as smaller highlighted pins.
 * Always renders (with empty features when none) to maintain consistent layer ordering.
 */
export function RelatedPlacesLayer({ relatedPlaces, onClick }: RelatedPlacesLayerProps) {
  const data = useMemo(() => toGeoJSON(relatedPlaces), [relatedPlaces]);

  const handleClick = useCallback(
    (e: MapMouseEvent) => {
      if (!onClick) return;
      const clickedFeature = e.features?.[0];
      const placeId = clickedFeature?.properties?.id as string | undefined;
      if (!placeId) return;

      const place = relatedPlaces.find((p) => p.id === placeId);
      if (place) {
        onClick(place);
      }
    },
    [relatedPlaces, onClick],
  );

  useMapEvent("click", RELATED_PLACES_LAYER_ID, handleClick);
  useCursorOnHover(RELATED_PLACES_LAYER_ID);

  return (
    <Source id={RELATED_PLACES_SOURCE_ID} type="geojson" data={data}>
      <Layer id={RELATED_PLACES_LAYER_ID} type="symbol" layout={relatedPlacesLayout} />
    </Source>
  );
}
